import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Card, TextField, Typography, Skeleton, Tooltip } from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { HRApiWrapper } from 'api/HRApiWrapper';

const COLORS_BY_POSITION = [
  '#aec6e8', // light blue
  '#ffd966', // yellow
  '#92d050', // green
  '#e07b54', // orange
  '#b39ddb', // lavender
  '#80cbc4', // teal
  '#f48fb1', // pink
  '#ffcc80', // peach
  '#a5d6a7', // mint
  '#ef9a9a', // coral
];

const CATEGORY_HEADER_HEIGHT = 36;
const SKILL_HEADER_HEIGHT = 120;
const CELL_SIZE = 28;
const NAME_COL_WIDTH = 180;
const ROLE_COL_WIDTH = 140;

interface ProfileSkillEntry {
  id: number;
  idSkillLevel: number;
}

const SkillMatrixPage: React.FC = () => {
  const api: Api = useApi();
  const { employeeApi } = api;
  const hrApi = new HRApiWrapper(employeeApi);
  const toast = useToastMessageQueue();

  const [categories, setCategories] = useState<any[]>([]);
  const [skills, setSkills] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [levelColorMap, setLevelColorMap] = useState<Map<number, string>>(new Map());
  const [levelLabelMap, setLevelLabelMap] = useState<Map<number, string>>(new Map());
  const [profiles, setProfiles] = useState<any[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<Set<string>>(new Set());
  // Map<"profileId_skillId", {id, idSkillLevel}>
  const [skillMap, setSkillMap] = useState<Map<string, ProfileSkillEntry>>(new Map());

  const levelIds = useRef<number[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, skillRes, levelRes, profileRes] = await Promise.all([
        hrApi.getSkillCategories(),
        hrApi.getSkills('1', 1, 1000),
        hrApi.getSkillLevels(),
        hrApi.getAllProfilesWithSkills(),
      ]);

      const cats: any[] = catRes.data?.data ?? [];
      const allSkills: any[] = skillRes.data?.data ?? [];
      const allLevels: any[] = levelRes.data?.data ?? [];
      const allProfiles: any[] = profileRes.data?.data ?? [];

      // Sort levels by id for cycling order
      const sortedLevels = [...allLevels].sort((a, b) => a.id - b.id);
      levelIds.current = sortedLevels.map((l) => l.id);

      // Build dynamic color/label maps from actual DB IDs
      const colorMap = new Map<number, string>();
      const labelMap = new Map<number, string>();
      sortedLevels.forEach((l, idx) => {
        colorMap.set(l.id, COLORS_BY_POSITION[idx] ?? '#aec6e8');
        labelMap.set(l.id, l.name);
      });
      setLevelColorMap(colorMap);
      setLevelLabelMap(labelMap);

      // Only show categories that have at least one skill
      const skillsByCat = new Map<number, any[]>();
      for (const sk of allSkills) {
        if (!sk.isDeleted) {
          const list = skillsByCat.get(sk.idSkillCategory) ?? [];
          list.push(sk);
          skillsByCat.set(sk.idSkillCategory, list);
        }
      }
      const activeCats = cats
        .filter((c) => !c.isDeleted && skillsByCat.has(c.id))
        .map((c) => ({ ...c, skills: skillsByCat.get(c.id) ?? [] }));

      // Build flat ordered skill list (same order as columns)
      const orderedSkills: any[] = [];
      for (const cat of activeCats) orderedSkills.push(...cat.skills);

      // Build lookup map
      const map = new Map<string, ProfileSkillEntry>();
      for (const p of allProfiles) {
        for (const ps of p.profileSkills ?? []) {
          if (!ps.isDeleted) {
            map.set(`${p.id}_${ps.idSkill}`, { id: ps.id, idSkillLevel: ps.idSkillLevel });
          }
        }
      }

      setCategories(activeCats);
      setSkills(orderedSkills);
      setLevels(sortedLevels);
      setProfiles(allProfiles);
      setSkillMap(map);
    } catch (e) {
      handleAPIError(e, toast, {});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCellClick = useCallback(async (profileId: number, skillId: number) => {
    const key = `${profileId}_${skillId}`;
    if (pending.has(key)) return;

    const existing = skillMap.get(key);
    const ids = levelIds.current;
    if (!ids.length) return;

    let nextLevelId: number | null = null;
    if (!existing) {
      nextLevelId = ids[0];
    } else {
      const idx = ids.indexOf(existing.idSkillLevel);
      if (idx === -1 || idx === ids.length - 1) {
        nextLevelId = null; // remove
      } else {
        nextLevelId = ids[idx + 1];
      }
    }

    // Optimistic update
    setPending((prev) => new Set(prev).add(key));
    const prevMap = new Map(skillMap);

    if (nextLevelId === null && existing) {
      const newMap = new Map(skillMap);
      newMap.delete(key);
      setSkillMap(newMap);
    } else if (nextLevelId !== null) {
      const newMap = new Map(skillMap);
      newMap.set(key, { id: existing?.id ?? 0, idSkillLevel: nextLevelId });
      setSkillMap(newMap);
    }

    try {
      if (!existing && nextLevelId !== null) {
        const res = await hrApi.addSkillToProfile('1', { idProfile: profileId, idSkill: skillId, idSkillLevel: nextLevelId });
        const created = res.data?.data;
        if (created) {
          setSkillMap((prev) => {
            const m = new Map(prev);
            m.set(key, { id: created.id, idSkillLevel: created.idSkillLevel });
            return m;
          });
        }
      } else if (existing && nextLevelId !== null) {
        await hrApi.updateProfileSkill('1', { id: existing.id, idSkillLevel: nextLevelId });
      } else if (existing && nextLevelId === null) {
        await hrApi.removeSkillFromProfile('1', existing.id);
      }
    } catch (e) {
      // Revert on error
      setSkillMap(prevMap);
      handleAPIError(e, toast, {});
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [skillMap, pending, hrApi, toast]);

  const filteredProfiles = profiles.filter((p) => {
    if (!filter) return true;
    const name = `${p.employee?.name ?? ''} ${p.employee?.lastName ?? ''}`.toLowerCase();
    return name.includes(filter.toLowerCase());
  });

  const stickyHeaderBase = { position: 'sticky', zIndex: 2, backgroundColor: '#fff' };

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" sx={{ mb: 2 }}>
          {intl.get('mainNavigation.humanResources.skillMatrix') || 'Skill Matrix'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <TextField
            size="small"
            placeholder={intl.get('hr.profile.selectEmployee') || 'Filter by name...'}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ width: 280 }}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {levels.map((l, idx) => (
              <Box key={l.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: COLORS_BY_POSITION[idx] ?? '#aec6e8', border: '1px solid #ccc', borderRadius: 0.5 }} />
                <Typography variant="caption">{l.name}</Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: 0.5 }} />
              <Typography variant="caption">{intl.get('common.none') || 'None'}</Typography>
            </Box>
          </Box>
        </Box>
      {loading ? (
        <>
          <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={400} />
        </>
      ) : (
      <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 260px)', border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            {/* Category row */}
            <tr>
              <th
                style={{
                  ...stickyHeaderBase as any,
                  top: 0,
                  left: 0,
                  zIndex: 3,
                  width: NAME_COL_WIDTH,
                  minWidth: NAME_COL_WIDTH,
                  height: CATEGORY_HEADER_HEIGHT,
                  background: '#fff',
                  border: '1px solid #e0e0e0',
                }}
              />
              <th
                style={{
                  ...stickyHeaderBase as any,
                  top: 0,
                  left: NAME_COL_WIDTH,
                  zIndex: 3,
                  width: ROLE_COL_WIDTH,
                  minWidth: ROLE_COL_WIDTH,
                  height: CATEGORY_HEADER_HEIGHT,
                  background: '#fff',
                  border: '1px solid #e0e0e0',
                }}
              />
              {categories.map((cat) => (
                <th
                  key={cat.id}
                  colSpan={cat.skills.length}
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    background: '#424242',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    textAlign: 'center',
                    height: CATEGORY_HEADER_HEIGHT,
                    border: '1px solid #616161',
                    padding: '0 8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {cat.name}
                </th>
              ))}
            </tr>
            {/* Skill name row */}
            <tr>
              <th
                style={{
                  position: 'sticky',
                  top: CATEGORY_HEADER_HEIGHT,
                  left: 0,
                  zIndex: 3,
                  width: NAME_COL_WIDTH,
                  minWidth: NAME_COL_WIDTH,
                  height: SKILL_HEADER_HEIGHT,
                  background: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  fontSize: 12,
                  fontWeight: 600,
                  paddingLeft: 8,
                  textAlign: 'left',
                  verticalAlign: 'bottom',
                  paddingBottom: 6,
                }}
              >
                Employee
              </th>
              <th
                style={{
                  position: 'sticky',
                  top: CATEGORY_HEADER_HEIGHT,
                  left: NAME_COL_WIDTH,
                  zIndex: 3,
                  width: ROLE_COL_WIDTH,
                  minWidth: ROLE_COL_WIDTH,
                  height: SKILL_HEADER_HEIGHT,
                  background: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  fontSize: 12,
                  fontWeight: 600,
                  paddingLeft: 8,
                  textAlign: 'left',
                  verticalAlign: 'bottom',
                  paddingBottom: 6,
                }}
              >
                Role
              </th>
              {skills.map((sk) => (
                <th
                  key={sk.id}
                  style={{
                    position: 'sticky',
                    top: CATEGORY_HEADER_HEIGHT,
                    zIndex: 2,
                    width: CELL_SIZE,
                    minWidth: CELL_SIZE,
                    height: SKILL_HEADER_HEIGHT,
                    background: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    padding: 0,
                  }}
                >
                  <div
                    style={{
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      fontSize: 11,
                      fontWeight: 500,
                      padding: '4px 0',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxHeight: SKILL_HEADER_HEIGHT - 8,
                    }}
                  >
                    {sk.name}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredProfiles.map((profile) => (
              <tr key={profile.id}>
                <td
                  style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    background: '#fff',
                    border: '1px solid #e0e0e0',
                    fontSize: 12,
                    width: NAME_COL_WIDTH,
                    minWidth: NAME_COL_WIDTH,
                    padding: '0 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    height: CELL_SIZE + 2,
                  }}
                >
                  {profile.employee?.name} {profile.employee?.lastName}
                </td>
                <td
                  style={{
                    position: 'sticky',
                    left: NAME_COL_WIDTH,
                    zIndex: 1,
                    background: '#fff',
                    border: '1px solid #e0e0e0',
                    fontSize: 11,
                    color: '#666',
                    width: ROLE_COL_WIDTH,
                    minWidth: ROLE_COL_WIDTH,
                    padding: '0 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {profile.role?.name ?? ''}
                </td>
                {skills.map((sk) => {
                  const key = `${profile.id}_${sk.id}`;
                  const entry = skillMap.get(key);
                  const levelId = entry?.idSkillLevel;
                  const isPending = pending.has(key);
                  const bg = levelId ? (levelColorMap.get(levelId) ?? '#fff') : '#fff';
                  const label = levelId ? (levelLabelMap.get(levelId) ?? '') : '';
                  return (
                    <Tooltip key={sk.id} title={label ? `${sk.name}: ${label}` : sk.name} placement="top">
                      <td
                        onClick={() => handleCellClick(profile.id, sk.id)}
                        style={{
                          width: CELL_SIZE,
                          minWidth: CELL_SIZE,
                          height: CELL_SIZE + 2,
                          backgroundColor: bg,
                          border: '1px solid #e0e0e0',
                          cursor: 'pointer',
                          opacity: isPending ? 0.5 : 1,
                          transition: 'background-color 0.15s',
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </tr>
            ))}
            {filteredProfiles.length === 0 && (
              <tr>
                <td
                  colSpan={2 + skills.length}
                  style={{ textAlign: 'center', padding: 24, color: '#999', fontSize: 13 }}
                >
                  No profiles found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
      )}
      </Box>
    </Card>
  );
};

export default SkillMatrixPage;
